import {
  Component,
  inject,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  ElementRef,
  signal,
  computed,
  afterNextRender,
  Injector,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { ComponentService } from '../../services/component.service';
import { ChatService } from '../../services/chat.service';
import { TenantComponents } from '../../types/Tenant';
import { ChatPanelComponent } from './chat-panel/chat-panel.component';

@Component({
  selector: 'app-chatbot',
  imports: [MatIconModule, ChatPanelComponent],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-open]': 'isOpen',
    '(transitionend)': 'onPaneTransitionEnd($event)',
    '[style.--chat-pane-width.px]': 'paneWidth()',
    '[class.is-resizing]': 'isResizing()',
  },
})
export class ChatbotComponent implements OnInit, OnDestroy {
  private readonly componentService = inject(ComponentService);
  private readonly chatService = inject(ChatService);
  private readonly translateService = inject(TranslateService);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroy$ = new Subject<void>();
  private readonly injector = inject(Injector);
  private suppressTabClick = false;
  private dragStart?: { pointerId: number; y: number; top: number };
  private resizeStart?: { pointerId: number; x: number; width: number };
  private readonly tabHeight = 80;
  private readonly tabEdgeSpacing = 8;

  public readonly paneWidth = signal<number | null>(null);
  public readonly isResizing = signal(false);
  public readonly tabPosition = signal(80);
  public readonly tabTop = computed(() => {
    const edge = this.tabEdgeSpacing;
    return `clamp(${edge}px, ${this.tabPosition()}dvh, max(${edge}px, calc(100dvh - ${this.tabHeight + edge}px)))`;
  });
  public isOpen = false;
  public showPanel = false;
  public isVisible = false;
  public resizeLabel = '';
  public resizeHint = '';
  public tabLabel = '';
  public tabHint = '';

  public ngOnInit(): void {
    this.isVisible = this.componentService.isActive(TenantComponents.CHATBOT);
    if (this.isVisible) {
      this.chatService.refreshConversationTokenUsage();
    }
    this.updateLabels();
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateLabels());
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toggleChat(focusPanelOnOpen = true): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.showPanel = true;
      this.chatService.refreshConversationTokenUsage();
    } else if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showPanel = false;
    }

    const focusSelector = this.isOpen ? (focusPanelOnOpen ? '.chat-panel' : null) : '.chat-fab';
    if (focusSelector) {
      afterNextRender(
        () => {
          this.element.nativeElement.querySelector<HTMLElement>(focusSelector)?.focus();
        },
        { injector: this.injector },
      );
    }
  }

  public onTabClick(event: MouseEvent): void {
    if (this.suppressTabClick && event.detail > 0) {
      this.suppressTabClick = false;
      event.preventDefault();
      return;
    }
    this.suppressTabClick = false;
    this.toggleChat(event.detail === 0);
  }

  public onPaneTransitionEnd(event: TransitionEvent): void {
    if (event.target === this.element.nativeElement && event.propertyName === 'width' && !this.isOpen) {
      this.showPanel = false;
    }
  }

  public onTabDragStart(event: PointerEvent): void {
    if (event.button !== 0 || !(event.currentTarget instanceof HTMLElement)) {
      return;
    }
    event.preventDefault();
    this.suppressTabClick = false;
    event.currentTarget.blur();
    event.currentTarget.setPointerCapture(event.pointerId);
    this.dragStart = {
      pointerId: event.pointerId,
      y: event.clientY,
      top: this.getTabTop(event.currentTarget),
    };
  }

  public onTabDragMove(event: PointerEvent): void {
    if (this.dragStart?.pointerId === event.pointerId) {
      const deltaY = event.clientY - this.dragStart.y;
      if (Math.abs(deltaY) > 4) {
        this.suppressTabClick = true;
      }
      if (this.suppressTabClick) {
        this.setTabTop(this.dragStart.top + deltaY);
      }
    }
  }

  public onResizeStart(event: PointerEvent): void {
    if (event.button !== 0 || !(event.currentTarget instanceof HTMLElement)) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    this.isResizing.set(true);
    this.resizeStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      width: this.element.nativeElement.getBoundingClientRect().width,
    };
  }

  public onResizeMove(event: PointerEvent): void {
    if (this.resizeStart?.pointerId === event.pointerId) {
      this.setPaneWidth(this.resizeStart.width + this.resizeStart.x - event.clientX);
    }
  }

  public onResizeKeydown(event: KeyboardEvent): void {
    const direction = event.key === 'ArrowLeft' ? 1 : event.key === 'ArrowRight' ? -1 : 0;
    if (direction !== 0) {
      event.preventDefault();
      const width = Math.min(
        this.paneWidth() ?? this.element.nativeElement.getBoundingClientRect().width,
        window.innerWidth * 0.65,
      );
      this.setPaneWidth(width + direction * 20);
    }
  }

  public onPointerEnd(event: PointerEvent): void {
    if (this.dragStart?.pointerId === event.pointerId) {
      this.dragStart = undefined;
    }
    if (this.resizeStart?.pointerId === event.pointerId) {
      this.resizeStart = undefined;
      this.isResizing.set(false);
    }
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  private getTabTop(tab: HTMLElement): number {
    return tab.getBoundingClientRect().top - this.element.nativeElement.getBoundingClientRect().top;
  }

  private setTabTop(top: number): void {
    const height = this.element.nativeElement.getBoundingClientRect().height || window.innerHeight;
    const maxTop = Math.max(this.tabEdgeSpacing, height - this.tabHeight - this.tabEdgeSpacing);
    const clamped = Math.max(this.tabEdgeSpacing, Math.min(top, maxTop));
    this.tabPosition.set((clamped / height) * 100);
  }

  private setPaneWidth(width: number): void {
    const maxWidth = window.innerWidth * 0.65;
    this.paneWidth.set(Math.round(Math.min(maxWidth, Math.max(320, width))));
  }

  private updateLabels(): void {
    this.resizeLabel = this.translateService.instant('chatbot.resize_pane');
    this.resizeHint = this.translateService.instant('chatbot.resize_pane_hint');
    this.tabLabel = this.translateService.instant('chatbot.open_and_move_tab');
    this.tabHint = this.translateService.instant('chatbot.open_and_move_tab_hint');
  }
}
